import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useTheme,
} from "@mui/material";

interface PricingRow {
  days: string;
  monthly?: string;
  yearly?: string;
  academic?: string;
  meals?: string;
  fullBoard?: string;
  withoutMeals?: string;
}

interface PricingTableProps {
  currentPackage: string;
  activeTab: number;
  academicYear: string;
  academicYearKey: string;
}

const PricingTable: React.FC<PricingTableProps> = ({
  currentPackage,
  activeTab,
  academicYear,
  academicYearKey,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color={theme.palette.primary.light}
        textAlign="center"
        mb={4}
      >
        {t(`PricingPage.PricingFrame.${currentPackage}.titleTable`)} - {academicYear}
      </Typography>

      {Object.entries(
        t(`PricingPage.PricingFrame.${currentPackage}.pricingTable.${academicYearKey}`, {
          returnObjects: true,
        })
      ).map(([type, rows], index) => (
        <Box key={index} mb={4}>
          <Typography
            variant="h5"
            fontWeight={600}
            gutterBottom
            color={theme.palette.text.secondary}
          >
            {type === "fullDay"
              ? t("PricingPage.PricingFrame.fullDay")
              : t("PricingPage.PricingFrame.halfDay")}
          </Typography>

          <TableContainer
            sx={{
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRight: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflowY: "hidden",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.light }}>
                  <TableCell
                    sx={{
                      backgroundColor: theme.palette.text.secondary,
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      borderRight: `2px solid ${theme.palette.text.secondary}`,
                    }}
                  >
                    {t("PricingPage.PricingFrame.headersTable.days")}
                  </TableCell>
                  {activeTab === 0 ? (
                    <>
                      <TableCell sx={{ color: theme.palette.primary.main }}>
                        {t("PricingPage.PricingFrame.headersTable.monthly")}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.primary.main }}>
                        {t("PricingPage.PricingFrame.headersTable.yearly")}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.primary.main }}>
                        {t("PricingPage.PricingFrame.headersTable.academic")}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.primary.main }}>
                        {t("PricingPage.PricingFrame.headersTable.meals")}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        {t("PricingPage.PricingFrame.headersTable.fullBoard")}
                      </TableCell>
                      <TableCell>
                        {t(
                          "PricingPage.PricingFrame.headersTable.withoutMeals"
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(rows as PricingRow[]).map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell
                      sx={{
                        borderRight: `2px solid ${theme.palette.text.secondary}`,
                      }}
                    >
                      {row.days}
                    </TableCell>
                    {activeTab === 0 ? (
                      <>
                        <TableCell>{row.monthly}</TableCell>
                        <TableCell>{row.yearly}</TableCell>
                        <TableCell>{row.academic}</TableCell>
                        <TableCell>{row.meals}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{row.fullBoard}</TableCell>
                        <TableCell>{row.withoutMeals}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </>
  );
};

export default PricingTable;
